'use client';

import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { css } from '@onbeam/business-styled-system/css';
import { vstack } from '@onbeam/business-styled-system/patterns';
import { calculateRows } from './MissionText.utils';
import { waitMs } from '@onbeam/utils';
import { MissionTextProps, MissionTextRowData } from './MissionText.types';
import { MissionTextRow } from './MissionTextRow';

export const MissionText = ({ paragraphs }: MissionTextProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [rowsPerParagraph, setRowsPerParagraph] = useState<
    MissionTextRowData[][]
  >([]);

  const recomputeRows = useCallback(async () => {
    if (!containerRef.current) return;
    setRowsPerParagraph([]);
    await waitMs(100);
    const newRows = calculateRows(containerRef.current);
    setRowsPerParagraph(newRows);
  }, []);

  useLayoutEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      resizeTimeoutRef.current = setTimeout(() => {
        recomputeRows();
      }, 150);
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [recomputeRows]);

  return (
    <div
      ref={containerRef}
      className={vstack({
        textStyle: 'subtitle-md',
        gap: '12',
        maxW: '[54rem]',
        mt: '10',
        md: { textStyle: 'subtitle-xl', gap: '20', mt: '16' },
      })}
    >
      {paragraphs.map((paragraph, paragraphIndex) => {
        const words = paragraph.split(' ');
        const rowData = rowsPerParagraph[paragraphIndex];

        const globalIndexOffset = rowsPerParagraph
          .slice(0, paragraphIndex)
          .reduce((acc, rows) => acc + rows.length, 0);

        return (
          <p key={paragraphIndex} data-paragraph-index={paragraphIndex}>
            {!!rowData?.length
              ? rowData.map(({ words, isInitiallyInView }, rowIndex) => (
                  <MissionTextRow
                    key={rowIndex}
                    index={globalIndexOffset + rowIndex}
                    words={words}
                    isInitiallyInView={isInitiallyInView}
                  />
                ))
              : words.map((word, wordIndex) => (
                  <span
                    key={wordIndex}
                    data-word={word}
                    className={css({
                      display: 'inline-block',
                      whiteSpace: 'pre',
                      opacity: 0,
                    })}
                  >
                    {word + (wordIndex < words.length - 1 ? ' ' : '')}
                  </span>
                ))}
          </p>
        );
      })}
    </div>
  );
};
